import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Icon, PageHeader } from '@carrent/ui'
import { useKbSearch, useKbSpaces } from '@carrent/domain'
import { readCache, refreshCache, watchNetwork } from '../../../offline/knowledge'

/**
 * База знаний водителя: выбор раздела.
 *
 * Не три колонки, как в консоли, а один поток: экран решает одну
 * задачу — довести до нужной инструкции за минимум касаний. Поиск
 * стоит первым, потому что водитель обычно ищет конкретное правило,
 * а не изучает структуру.
 */
export default function KbSpacesPage() {
  const navigate = useNavigate()
  const { spaces, loading, error } = useKbSpaces()
  const [query, setQuery] = useState('')
  const { results, loading: searching } = useKbSearch(query)

  // Закреплённые инструкции обновляются в фоне: при заходе в раздел
  // и когда возвращается связь.
  const [cache, setCache] = useState(readCache)
  useEffect(() => {
    refreshCache().then((c) => c && setCache(c))
    return watchNetwork()
  }, [])

  const searchMode = query.trim().length >= 2
  // Разделы не загрузились (обычно — нет сети), но сохранённое есть.
  const showOffline = Boolean(error) && (cache?.articles?.length > 0)

  return (
    <div className="p-page flex-col gap">
      <PageHeader title="База знаний" backTo="/more" />

      <label className="kb-m__search">
        <Icon name="search" size={18} aria-hidden="true" />
        <input
          className="kb-m__search-input"
          type="search"
          value={query}
          placeholder="Найти инструкцию"
          aria-label="Поиск по базе знаний"
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>

      {searchMode ? (
        <SearchResults
          results={results}
          loading={searching}
          spaces={spaces}
          onOpen={(hit) => {
            const space = spaces.find((s) => s.id === hit.space_id)
            if (space) navigate(`/kb/${space.key}/${hit.page_id}`)
          }}
        />
      ) : (
        <>
          {loading && <div className="kb-m__loading"><div className="spinner" /></div>}

          {showOffline && (
            <section>
              <p className="kb-m__offline">
                <Icon name="warning" size={15} aria-hidden="true" />
                Сети нет — доступны сохранённые инструкции
              </p>
              <nav className="more-list" aria-label="Сохранённые инструкции">
                {cache.articles.map((a) => (
                  <Link key={a.id} to={`/kb/${a.space_key}/${a.id}`}
                    className="more-list__item">
                    <Icon name="file" size={22} className="more-list__icon" />
                    <span className="more-list__text">
                      <span className="more-list__label">{a.title}</span>
                      <span className="more-list__hint">{a.space_name}</span>
                    </span>
                    <Icon name="chevronRight" size={18} className="more-list__chevron" />
                  </Link>
                ))}
              </nav>
            </section>
          )}

          {error && !showOffline && (
            <div className="kb-m__state">
              <Icon name="warning" size={28} aria-hidden="true" />
              <p className="kb-m__state-title">{error}</p>
              <p className="kb-m__state-hint">Проверьте связь и попробуйте ещё раз.</p>
            </div>
          )}

          {!loading && !error && spaces.length === 0 && (
            <div className="kb-m__state">
              <Icon name="file" size={28} aria-hidden="true" />
              <p className="kb-m__state-title">Пока нет доступных разделов</p>
              <p className="kb-m__state-hint">
                Доступ открывает администратор. Если инструкции нужны для работы —
                напишите в поддержку.
              </p>
            </div>
          )}

          {spaces.length > 0 && (
            <nav className="more-list" aria-label="Разделы базы знаний">
              {spaces.map((s) => (
                <Link key={s.id} to={`/kb/${s.key}`} className="more-list__item">
                  <Icon name={s.icon || 'file'} size={22} className="more-list__icon" />
                  <span className="more-list__text">
                    <span className="more-list__label">{s.name}</span>
                    {s.description && (
                      <span className="more-list__hint">{s.description}</span>
                    )}
                  </span>
                  <Icon name="chevronRight" size={18} className="more-list__chevron" />
                </Link>
              ))}
            </nav>
          )}
        </>
      )}
    </div>
  )
}

function SearchResults({ results, loading, spaces, onOpen }) {
  if (loading) {
    return <div className="kb-m__loading"><div className="spinner" /></div>
  }
  if (!results.length) {
    return (
      <div className="kb-m__state">
        <p className="kb-m__state-title">Ничего не нашлось</p>
        <p className="kb-m__state-hint">Попробуйте другое слово — например, «мойка» или «топливо».</p>
      </div>
    )
  }
  return (
    <ul className="kb-m__hits">
      {results.map((hit) => {
        const space = spaces.find((s) => s.id === hit.space_id)
        return (
          <li key={hit.page_id}>
            <button type="button" className="kb-m__hit" onClick={() => onOpen(hit)}>
              <span className="kb-m__hit-title">{hit.title}</span>
              {space && <span className="kb-m__hit-space">{space.name}</span>}
              <span className="kb-m__hit-snippet">
                {String(hit.snippet || '').replace(/<\/?b>/g, '')}
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
